import { useEffect, useRef, useState } from "react";
import { BarChart3, Crown, Medal, Trophy, Clock, Flag, ChevronDown, Check } from "lucide-react";
import apiClient from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, InlineLoader, EmptyState } from "@/components/common";
import { fmtLap } from "@/pages/MySessions";

const categories = [
  { key: "laptime", label: "Lap Times" },
  { key: "wins", label: "Wins" },
  { key: "hours_booked", label: "Most Active" },
  { key: "hours_racing", label: "Driven" },
  { key: "hours_flight", label: "Flown" },
  { key: "hours_vr", label: "In VR" },
  { key: "points", label: "Points" },
];

const rankColor = (rank) => {
  if (rank === 1) return "#ffb020";
  if (rank === 2) return "#cbd5e1";
  if (rank === 3) return "#f59e0b";
  return "#64748b";
};

function TrackDropdown({ tracks, track, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const selected = tracks.find((t) => t.track_id === track);
  const label = selected ? selected.display_name : "All Tracks";
  return (
    <div className="relative mb-6 w-full sm:w-80" ref={ref} data-testid="track-dropdown">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="track-dropdown-trigger"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all"
        style={{ background: "linear-gradient(180deg,rgba(0,229,255,0.10),rgba(8,11,22,0.75))", border: `1px solid ${open ? "rgba(0,229,255,0.6)" : "rgba(120,150,220,0.18)"}` }}
      >
        <span className="inline-flex items-center gap-2.5 min-w-0">
          <span className="w-7 h-7 rounded-lg grid place-items-center shrink-0" style={{ background: "rgba(0,229,255,0.14)", border: "1px solid rgba(0,229,255,0.4)" }}>
            <Flag className="w-3.5 h-3.5" style={{ color: "#00e5ff" }} />
          </span>
          <span className="flex flex-col items-start min-w-0">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 leading-none">Circuit</span>
            <span className="text-white font-display font-bold truncate max-w-full leading-tight">{label}</span>
          </span>
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full rounded-xl overflow-hidden shadow-2xl"
          style={{ background: "rgba(8,11,22,0.98)", border: "1px solid rgba(0,229,255,0.3)", backdropFilter: "blur(12px)" }}>
          <div className="max-h-72 overflow-y-auto py-1">
            <DropdownItem active={!track} onClick={() => { onChange(null); setOpen(false); }} label="All Tracks" testId="track-opt-all" />
            {tracks.map((t) => (
              <DropdownItem key={t.track_id} active={track === t.track_id}
                onClick={() => { onChange(t.track_id); setOpen(false); }}
                label={t.display_name} testId={`track-opt-${t.track_id}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownItem({ active, onClick, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-white/5"
      style={active ? { background: "rgba(0,229,255,0.10)" } : {}}>
      <span className="inline-flex items-center gap-2 min-w-0">
        <Flag className="w-3.5 h-3.5 shrink-0" style={{ color: active ? "#00e5ff" : "#64748b" }} />
        <span className={`truncate ${active ? "text-white font-semibold" : "text-slate-300"}`}>{label}</span>
      </span>
      {active && <Check className="w-4 h-4 shrink-0" style={{ color: "#00e5ff" }} />}
    </button>
  );
}

export default function Leaderboards() {
  const { user } = useAuth();
  const [category, setCategory] = useState("laptime");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [track, setTrack] = useState(null);

  useEffect(() => {
    apiClient.get("/leaderboards/tracks").then(({ data }) => setTracks(data || [])).catch(() => setTracks([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { category, limit: 50 };
    if (category === "laptime" && track) params.track = track;
    apiClient.get("/leaderboards", { params })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [category, track]);

  const metricFor = (row) => {
    if (row.value_label) return row.value_label;
    if (category === "laptime") return fmtLap(row.value);
    if (category === "points") return `${(row.score ?? row.value ?? 0).toLocaleString()} pts`;
    return `${row.value ?? 0}`;
  };

  const hoursLabel = { hours_booked: "Lounge time", hours_racing: "Racing", hours_flight: "Flight", hours_vr: "VR" };

  const showTracks = category === "laptime" && tracks.length > 0;

  return (
    <div>
      <PageHeader title="Leaderboards" subtitle="See who dominates the arena." />

      <div className="flex flex-wrap gap-2 mb-4">
        {categories.map((c) => (
          <button key={c.key} onClick={() => { setCategory(c.key); if (c.key !== "laptime") setTrack(null); }}
            data-testid={`leaderboard-tab-${c.key}`}
            className="px-5 py-2.5 rounded-xl font-display font-semibold uppercase tracking-wide text-sm transition-all"
            style={category === c.key
              ? { background: "linear-gradient(90deg,#00e5ff,#8b5cf6)", color: "#05060d" }
              : { background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)", color: "#94a3b8" }}>
            {c.label}
          </button>
        ))}
      </div>

      {showTracks && (
        <TrackDropdown tracks={tracks} track={track} onChange={setTrack} />
      )}

      {loading ? (
        <InlineLoader label="Loading rankings" />
      ) : rows.length === 0 ? (
        <EmptyState icon={BarChart3} title="No rankings yet" subtitle="Play sessions and earn points to climb the board." />
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          {rows.map((row) => {
            const isMe = row.id === user?.id;
            return (
              <div key={row.id}
                className="flex items-center gap-4 px-5 py-4 border-b border-white/5 last:border-0"
                style={isMe ? { background: "rgba(0,229,255,0.08)" } : {}}
                data-testid={`leaderboard-row-${row.rank}`}>
                <div className="w-10 flex justify-center">
                  {row.rank <= 3 ? (
                    <Medal className="w-6 h-6" style={{ color: rankColor(row.rank) }} />
                  ) : (
                    <span className="font-display font-bold text-slate-500">#{row.rank}</span>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold text-sm text-black shrink-0"
                  style={{ background: "linear-gradient(135deg,#00e5ff,#8b5cf6)" }}>
                  {row.username?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate flex items-center gap-2">
                    {row.username} {isMe && <span className="chip" style={{ background: "rgba(0,229,255,0.15)", border: "1px solid rgba(0,229,255,0.4)", color: "#00e5ff" }}>You</span>}
                  </p>
                  <p className="text-slate-500 text-xs flex items-center gap-2">
                    {category === "laptime" && row.track ? (
                      <><Flag className="w-3 h-3" /> {row.track}{row.car ? ` · ${row.car}` : ""}</>
                    ) : category === "wins" ? (
                      <><Trophy className="w-3 h-3" /> Tournament winner</>
                    ) : category.startsWith("hours") ? (
                      <><Clock className="w-3 h-3" /> {hoursLabel[category] || "Lounge time"}</>
                    ) : (
                      <><Crown className="w-3 h-3" /> Lvl {row.level ?? "—"}{row.win_rate ? ` · ${row.win_rate}% WR` : ""}</>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold neon-cyan-text">{metricFor(row)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
